import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiX, FiCheck, FiSliders } from 'react-icons/fi';
import { heroSlidesApi, getImageUrl } from '../../api/phpApi';
import ImageUploadField from './ImageUploadField';
import './AdminManage.css';

export default function AdminHeroSlides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    caption: '',
    button_text: '',
    button_url: '',
    order_position: 1,
    is_active: true
  });

  // Fetch slides
  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      setLoading(true);
      const data = await heroSlidesApi.getAll();
      setSlides(data || []);
    } catch (err) {
      setError('Erreur de chargement des slides: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle add/edit submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      setError("Veuillez sélectionner ou importer une image pour le slide");
      return;
    }

    try {
      if (editingId) {
        await heroSlidesApi.update(editingId, formData);
        setSuccess('Slide mis à jour avec succès !');
      } else {
        await heroSlidesApi.create(formData);
        setSuccess('Nouveau slide ajouté avec succès !');
      }
      setError('');
      setTimeout(() => setSuccess(''), 3000);
      resetForm();
      loadSlides();
    } catch (err) {
      setError('Erreur lors de la sauvegarde du slide: ' + err.message);
    }
  };

  // Handle edit
  const handleEdit = (slide) => {
    setFormData({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      image_url: slide.image_url || slide.src || '',
      caption: slide.caption || '',
      button_text: slide.button_text || '',
      button_url: slide.button_url || '',
      order_position: slide.order_position || 1,
      is_active: slide.is_active !== false
    });
    setEditingId(slide.id);
    setIsFormOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Voulez-vous vraiment supprimer ce slide d\'accueil ?')) {
      try {
        await heroSlidesApi.delete(id);
        setSuccess('Slide supprimé avec succès');
        setTimeout(() => setSuccess(''), 2500);
        loadSlides();
      } catch (err) {
        setError('Erreur lors de la suppression: ' + err.message);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      image_url: '',
      caption: '',
      button_text: '',
      button_url: '',
      order_position: slides.length + 1,
      is_active: true
    });
    setEditingId(null);
    setIsFormOpen(false);
  };

  if (loading) return <div className="admin-loading" style={{ padding: 20 }}>Chargement des slides...</div>;

  return (
    <div className="admin-manage-card" style={{ background: '#ffffff', borderRadius: 8, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {/* Section Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 16, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#1b1e4f', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiSliders /> Slides de la Page d'Accueil (Hero Section)
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.88rem' }}>
            Gérez les images d'arrière-plan, titres et légendes du carrousel de la page d'accueil
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setIsFormOpen(true);
          }}
          style={{
            background: '#1b1e4f',
            color: 'white',
            border: 'none',
            padding: '9px 18px',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <FiPlus size={16} /> Ajouter un Slide
        </button>
      </div>

      {success && (
        <div style={{ background: '#d1fae5', border: '1px solid #10b981', color: '#065f46', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiCheck size={16} /> {success}
        </div>
      )}

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #ef4444', color: '#991b1b', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Form Modal / Container */}
      {isFormOpen && (
        <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>
              {editingId ? 'Modifier le Slide' : 'Ajouter un Nouveau Slide d\'Accueil'}
            </h3>
            <button onClick={resetForm} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
              <FiX size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Image Upload Component */}
            <ImageUploadField
              label="Image de fond du Slide (Upload image obligatoire) *"
              value={formData.image_url}
              onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
              helpText="Importez une image haute définition (1920x1080 recommandé, max 8 Mo)"
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Titre du Slide
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Ex: GSIDA 2026"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Sous-titre
                </label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleInputChange}
                  placeholder="Ex: Innovation & Gouvernance"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                Légende affichée sous le titre principal
              </label>
              <textarea
                name="caption"
                value={formData.caption}
                onChange={handleInputChange}
                rows="2"
                placeholder="Ex: GSIDA – Gouvernance, Innovation, Développement en Afrique"
                style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 16, marginBottom: 16, alignItems: 'center' }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Texte du Bouton
                </label>
                <input
                  type="text"
                  name="button_text"
                  value={formData.button_text}
                  onChange={handleInputChange}
                  placeholder="Ex: QUI SOMMES NOUS"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Lien de redirection
                </label>
                <input
                  type="text"
                  name="button_url"
                  value={formData.button_url}
                  onChange={handleInputChange}
                  placeholder="Ex: /about#presentation"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Ordre
                </label>
                <input
                  type="number"
                  name="order_position"
                  value={formData.order_position}
                  onChange={handleInputChange}
                  min="1"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button
                type="button"
                onClick={resetForm}
                style={{ background: '#e2e8f0', color: '#475569', border: 'none', padding: '9px 18px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button
                type="submit"
                style={{ background: '#1b1e4f', color: 'white', border: 'none', padding: '9px 22px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <FiSave size={15} /> {editingId ? 'Mettre à jour le Slide' : 'Créer et activer le Slide'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Grid of Slides */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
        {slides.map((slide, index) => {
          const imgDisplay = getImageUrl(slide.image_url || slide.src);
          return (
            <div
              key={slide.id || index}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                overflow: 'hidden',
                background: '#ffffff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <div style={{ height: 160, background: '#0f172a', position: 'relative', overflow: 'hidden' }}>
                  {imgDisplay ? (
                    <img
                      src={imgDisplay}
                      alt={slide.title || 'Slide'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                      Pas d'image
                    </div>
                  )}
                  <span style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.65)', color: 'white', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                    Slide #{slide.order_position || index + 1}
                  </span>
                </div>

                <div style={{ padding: 14 }}>
                  <h4 style={{ margin: '0 0 6px 0', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                    {slide.title || `Slide ${index + 1}`}
                  </h4>
                  <p style={{ margin: 0, fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
                    {slide.caption || slide.subtitle || 'Aucune légende'}
                  </p>
                </div>
              </div>

              <div style={{ padding: '10px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#fafafa' }}>
                <button
                  onClick={() => handleEdit(slide)}
                  style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <FiEdit2 size={13} /> Modifier
                </button>
                <button
                  onClick={() => handleDelete(slide.id)}
                  style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <FiTrash2 size={13} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}